import { useEffect, useRef, useState, useMemo } from "react";
import { allowOrWarn, Util } from '../../util/Util';
import PendingModal from "./PendingModal";
import { useAuth } from "../../hooks/useAuth";
import { useData } from "../../hooks/useData";
import { periodRequestConfig } from "../../hooks/useDateRange";
import { useUI } from "../../providers/MoleUIContext";
import { toast } from "react-toastify";
import { LocalStorageKeys } from "../../util/constants";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { Box, IconButton, ListItemIcon, MenuItem, Tooltip } from "@mui/material";
import { AddCircleOutline, OpenInNew, Refresh } from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import TableView from "../../components/TableView";
import { MRT_ColumnDef } from "material-react-table";
import ExportMenuButton from "../../components/ExportMenuButton";
import { PendingDepositDetailPanel } from "./pendingDepositDetailShared";
import { NUMBER_FILTER_OPTIONS } from "../../util/constants";
import { parseISODay } from "../../util/businessClock";

import { roundQty } from "../../util/quantity";
import { canRecord } from '../../util/access';
const Pending = () => {
	const [toView, setToView] = useState<any>(null);
	const [createOpen, setCreateOpen] = useState(false);

	const { api, session, access } = useAuth();
	const { setLoadingMessage, t } = useUI();
	const { data, lastUpdate, loadFromIndexedDB, saveFromServer, dateRange, setDateRange, loading, setLoading } = useData(LocalStorageKeys.PENDING_DEPOSIT_RECORDS);

	const toastIdRef = useRef<string | number>(-1);
	const firstFetchRef = useRef(false);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			const loaded = await loadFromIndexedDB();
			if (cancelled || firstFetchRef.current) return;
			firstFetchRef.current = true;
			if (!loaded) fetchData();
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const fetchData = async (useLoadModal: boolean = true, forceUpdate: boolean = false) => {

		setLoading(true);

		const res = await api.get("/deposits/pending", {
			...(periodRequestConfig(dateRange))
		});

		if (res) {
			if (res.status === 200) {
				saveFromServer(res.data, dateRange, forceUpdate);
				toast.update(toastIdRef.current, {
					render: t("common:toast.success.refresh"),
					type: "success",
					isLoading: false,
					autoClose: 3000,
				});
			} else {
				console.error("Failed to fetch data:", res);
				toast.dismiss(toastIdRef.current);
			}
		} else {
			toast.dismiss(toastIdRef.current);
		}

		setLoading(false);
	};

	const handleDepositSuccess = () => {
		toastIdRef.current = toast.loading(t("common:toast.loading.depositValidated"));
		fetchData(false, true);
	};

	const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
		{
			header: t("common:tables.date"),
			id: "date",
			accessorFn: (row: any) => parseISODay(row.date),
			Cell: ({ cell }: { cell: any }) => Util.formatDate(cell.getValue(), "medium"),
			filterVariant: 'date-range',
			size: 140,
		},
		{
			header: t("common:deposits.pendingDetail.income"),
			accessorKey: "income",
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			header: t("common:deposits.pendingDetail.expenses"),
			accessorKey: "expenses",
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			header: t("common:deposits.pendingDetail.totalPending"),
			accessorKey: "total",
			Cell: ({ cell }: { cell: any }) => (
				<Box sx={{ fontWeight: 700, color: 'warning.main' }}>
					{Util.formatNumber(cell.getValue())}
				</Box>
			),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum',
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);

				useEffect(() => {
					const handle = setTimeout(() => {
						let sum = 0;
						for (const row of table.getFilteredRowModel().rows) {
							const value = Number(row.original.total);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
					}, 0);
					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);

				return (
					<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
	], [t]);

	return (
		<>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<TableView
				stateKey={LocalStorageKeys.PENDING_DEPOSIT_RECORDS}
				posting="deposit"
					dataScope="pending_deposit"
					header={t("common:deposits.headers.pending")}
					data={data}
					columns={columns}
					includeTimestampColumns={false}
					totals={[
						{ key: "income", label: t("common:deposits.pendingDetail.income") },
						{ key: "expenses", label: t("common:deposits.pendingDetail.expenses") },
						{ key: "total", label: t("common:tables.total") },
					]}
					lastUpdated={lastUpdate}
					period={dateRange}
					setDateRangeFilter={setDateRange}
					isLoading={loading}
					renderDetailPanel={({ row }) => (
						<PendingDepositDetailPanel
							row={row.original}
							t={t}
							canOpen={allowOrWarn(canRecord(access, 'deposit'))}
							onOpen={() => setToView(row.original)}
						/>
					)}

					renderTopToolbarActions={(table) => (
						<Box sx={{ display: 'flex', gap: '0.5rem' }}>
							<Tooltip title={t("common:ui.tooltips.refreshData")}>
								<IconButton onClick={() => fetchData()} color="primary">
									<Refresh />
								</IconButton>
							</Tooltip>
							{allowOrWarn(canRecord(access, 'deposit')) && (
								<Tooltip title={t("common:deposits.modal.newTitle")}>
									<IconButton onClick={() => setCreateOpen(true)} color="success">
										<AddCircleOutline />
									</IconButton>
								</Tooltip>
							)}
							{
								[
									...new Set(
										table.getSelectedRowModel().flatRows.map((row: any) => row.original.date)
									),
								].length === 1 && (
									<Tooltip title={t("common:ui.tooltips.openPendingDeposit")}>
										<IconButton
											onClick={() => {
												if (allowOrWarn(canRecord(access, 'deposit'))) {
													setToView(table.getSelectedRowModel().flatRows[0].original);
												}
											}}
											color="success">
											<OpenInNew />
										</IconButton>
									</Tooltip>
								)
							}
							<ExportMenuButton
								prefix="pending_deposits"
								data={data}
								columns={columns}
								table={table}
							/>
						</Box>
					)}
					renderRowActionMenuItems={(row, closeMenu) => [
						!toView ? (
							<MenuItem
								key={0}
								onClick={() => {
									if (allowOrWarn(canRecord(access, 'deposit'))) {
										setToView(row.original);
									}
									closeMenu();
								}}
								sx={{
									color: 'primary.main',
								}}
							>
								<ListItemIcon sx={{
									color: 'primary.main',
								}}>
									<OpenInNew />
								</ListItemIcon>
								{t("common:ui.buttons.open")}
							</MenuItem>
						) : null
					]}
				/>

			</LocalizationProvider>

			{(toView || createOpen) && (
				<PendingModal
					isOpen={Boolean(toView || createOpen)}
					toggle={() => {
						setToView(null);
						setCreateOpen(false);
					}}
					bundle={toView || {}}
					onChange={handleDepositSuccess}
				/>
			)}
		</>
	);
};

export default Pending;